
import { defineStore } from "pinia";
import { BSortOptions, BTableColumn, BTableOptions, serializeSortBy, serializeTableOptionsForQueryString } from "@/components/BTable/BTable.types";
import { BFieldSelection, BFilter, BFilterableField, dateTimeOperations, listOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import axios from "axios";
import { ErrorDescription } from "./ErrorDescription";
import { UploadType, UploadedFileDetails, FetchValidationMessagesResponse } from "@/types";
import { computed, ref, watch } from "vue";
import { defaultValidationMessagesColumns, fileStatusFilters, getTypes, statusFilters } from "@/tableDefaults";
import { serializeFilters } from "@/components/BTable/HelperFunctions";
import { handleError } from "./storeHelper";
import { getHtmlContent } from "@/utils";

/*
https://pinia.vuejs.org/core-concepts/#Setup-Stores
In Setup Stores:

ref()s become state properties
computed()s become getters
function()s become actions
*/

export const useBaseStore = () => {
    // The following urls will get phased out in a later jira so that eventually well only have the apiUrl    
    const uploadUrlBase = 'api/v1/upload';
    const ewUrlBase = 'api/v1/errors-warnings';
    
    var apiUrl = ref('');
    var dataset = ref(undefined as (undefined | UploadType));
    var errorCodes = ref([] as any[]);
    var errorDescriptions = ref([] as ErrorDescription[]);
    
    watch(dataset, initialize);

    axios.interceptors.response.use(function (response) {
        // status codes within the range of 2xx cause this function to trigger
        if(typeof (response.data) == "string" && response.data.indexOf('sign-in') > -1){
            window.location.href= "signin.aspx";
        }
        else {
            return response;
        }        
      }, function (error) {
        // everything else goes here
        handleError(error);
        
        return Promise.reject(error);
      });
    
    const fileUploadColumns = [
        {
            key: "id",
            text: "ID",
            sortable: false,
            columnClass: [hiddenCssClass(true)],
            class: (item) => [hiddenCssClass(true)],
            value: (item) => item["id"],
        },
        {
            key: "agency",
            text: "Agency",
            sortable: false,
            value: (item) => item.agency,
            filterable: {
                label: "Agency",
                property: "agency",
                operations: listOperations,
                editorComponent: "list-editor"
            },
            columnType: "primary"
        },
        {
            key: "uploadedBy",
            text: "Uploaded By",
            sortable: false,
            value: (item) => item["uploadedBy"],
            filterable: {
                label: "Uploaded By",
                property: "uploadedBy",
                operations: textOperations,
                editorComponent: "text-editor"
            },
            columnType: "primary"
        },
        {
            key: "uploadDate",
            text: "Upload Date",
            sortable: false,
            value: (item) => item["uploadDate"],
            filterable: {
                label: "Upload Date",
                property: "uploadDate",
                operations: dateTimeOperations,
                editorComponent: "date-editor"
            },
            columnType: "primary"
        },
        {
            key: "fileName",
            text: "File Name",
            sortable: false,
            value: (item) => item["fileName"],
            filterable: {
                label: "File Name",
                property: "fileName",
                operations: textOperations,
                editorComponent: "text-editor"
            },
            columnType: "primary"
        },
        {
            key: "submissionPeriod",
            text: "Submission Period",
            sortable: false,
            value: (item) => item["submissionPeriod"],
            filterable: undefined,
            columnType: "primary"
        },
        {
            key: "status",
            text: "Status",
            sortable: false,
            value: (item) => item["status"],
            filterable: {
                label: "Status",
                property: "status",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: fileStatusFilters
            },
            columnType: "primary"
        },
    ] as BTableColumn[];
    const validationMessageColumns = defaultValidationMessagesColumns;      

    const errorDescriptionsDownloadUrl = computed(()=>{
        return `${apiUrl.value}/error-descriptions/download/`;
    })
    

    var getDefaultFilterableFields = computed(() => {
        return [
            {
                property: "incidentNumber",
                label: "Incident Number",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "errorCode",
                label: "Error Code",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: errorCodes.value
            },
            {
                property: "errorType",
                label: "Type",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: getTypes
            }
        ] as BFilterableField[];
    
    });

    function initialize(){
        fetchErrorCodes();
        fetchErrorDescriptions();
    }
    
    function getErrorsAndWarningsColumns(onlyHasOneOri:boolean,showAdditionalColumns:boolean, onlyIncludePrimaryColumns:boolean) {
        var filteredColumns = [            
        {
            key: "agency",
            text: "Agency",
            sortable: false,
            value: (item) => item.agency,
            filterable: {
                label: "Agency",
                property: "agency",
                operations: listOperations,
                editorComponent: "list-editor"
            },
            columnType: "primary"    
        },
        {
            key: "ori",
            text: "Ori",
            sortable: true,
            value: (item) => item.ori,
            filterable: undefined,
            columnType: "secondary"    
        },
        {
            key: "incidentNumber",
            text: "Incident Number",
            sortable: onlyIncludePrimaryColumns,
            value: (item) => item["incidentNumber"],
            filterable: onlyIncludePrimaryColumns?{
                label: "Incident Number",
                property: "incidentNumber",
                operations: textOperations,
                editorComponent: "text-editor"
            }:undefined,
            columnType: "primary"
        },
        {
            key: "errorType",
            text: "Type",
            sortable: true,
            value: (item) => item["errorType"],
            filterable: {
                label: "Type",
                property: "errorType",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: getTypes
            },
            columnType: "primary"
        },
        {
            key: "status",
            text: "Status",
            sortable: true,
            value: (item) => item.status,
            filterable: {
                label: "Status",
                property: "status",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: statusFilters
            },
            columnType: "secondary"
        },
        {
            key: "uploadedBy",
            text: "Uploaded By",
            sortable: onlyIncludePrimaryColumns,
            value: (item) => item["uploadedBy"],
            filterable: onlyIncludePrimaryColumns? {
                label: "Uploaded By",
                property: "uploadedBy",
                operations: textOperations,
                editorComponent: "text-editor"
            }: undefined,
            columnType: "secondary"
        },
        {
            key: "uploadDate",
            text: "Upload Date",
            sortable: true,
            class: (item) => ["text-nowrap"],
            value: (item) => item["uploadDate"],
            filterable: {
                label: "Upload Date",
                property: "uploadDate",
                operations: dateTimeOperations,
                editorComponent: "date-editor"
            },
            columnType: "secondary"
        },
        {
            key: "fileName",
            text: "File Name",
            sortable: onlyIncludePrimaryColumns,
            class: (item) => ["text-wrap"],
            value: (item) => item["fileName"],
            filterable: onlyIncludePrimaryColumns?{
                label: "File Name",
                property: "fileName",
                operations: textOperations,
                editorComponent: "text-editor"
            }: undefined,
            columnType: "secondary"
        },
        {
            key: "errorCode",
            text: "Error Code",
            sortable: true,
            value: (item) => item["errorCode"],
            filterable: {
                label: "Error Code",
                property: "errorCode",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: errorCodes.value
            },
            columnType: "primary"
        },
        {
            key: "lineNumber",
            text: "Line Number",
            sortable: onlyIncludePrimaryColumns,
            value: (item) => item["lineNumber"],
            columnClass:["d-none"],
            class: (item) => ["d-none"],
            filterable: undefined,
            columnType: "primary"
        },
        {
            key: "description",
            text: "Description",
            sortable: onlyIncludePrimaryColumns,
            class: (item) => ["text-wrap"],
            value: (item) => item.description,
            filterable: onlyIncludePrimaryColumns? {
                label: "Description",
                property: "description",
                operations: textOperations,
                editorComponent: "text-editor"
            }: undefined,
            columnType: "primary"
        },
        {
            key: "vendorName",
            text: "Vendor Name",
            sortable: false,
            value: (item) => item["vendorName"],
            filterable: undefined,
            columnType: "secondary"
        },
        {
            key: "acknowledgedBy",
            text: "Actioned By",
            sortable: onlyIncludePrimaryColumns,
            value: (item) => item["acknowledgedBy"],
            filterable: onlyIncludePrimaryColumns?{
                label: "Actioned By",
                property: "acknowledgedBy",
                operations: textOperations,
                editorComponent: "text-editor"
            }: undefined,
            columnType: "tertiary"
        },
        {
            key: "acknowledgedOn",
            text: "Action Date",
            sortable: onlyIncludePrimaryColumns,
            class: (item) => ["text-nowrap"],
            value: (item) => item["acknowledgedOn"],
            filterable: onlyIncludePrimaryColumns? {
                property: "acknowledgedOn",
                label: "Action Date",
                operations: dateTimeOperations,
                            editorComponent: "date-editor"
            } : undefined,
            columnType: "tertiary"
        },
        {
            key: "explanation",
            text: "Explanation/Justification",
            sortable: onlyIncludePrimaryColumns,
            class: (item) => ["text-wrap"],
            value: (item) => item["explanation"],
            filterable: onlyIncludePrimaryColumns ? {
                label: "Explanation/Justification",
                property: "explanation",
                operations: textOperations,
                editorComponent: "text-editor"
            }: undefined,
            columnType: "tertiary"
        }
        ] as BTableColumn[];

        if(onlyIncludePrimaryColumns){
            filteredColumns = filteredColumns.filter((col:BTableColumn) => col.columnType == "primary");
        }

        if(onlyHasOneOri) {
            filteredColumns = filteredColumns.filter((col:BTableColumn) => col.key != "agency");
        }
        
        if(!showAdditionalColumns) {
            filteredColumns = filteredColumns.filter((col:BTableColumn) => col.key != "acknowledgedBy" && col.key != "acknowledgedOn" && col.key != "explanation");
        }
        
        return filteredColumns;
    };

    function getFilterableFieldsForErrorsAndWarnings(onlyHasOneOri:boolean,showAdditionalColumns:boolean, onlyIncludePrimaryColumns:boolean){
        var columns = getErrorsAndWarningsColumns(onlyHasOneOri,showAdditionalColumns, onlyIncludePrimaryColumns);
        var result = [] as BFilterableField[];

        columns.filter((col:BTableColumn) => col.filterable != undefined).map((col:BTableColumn)=> result.push(col.filterable as BFilterableField));

        return result;
    }

    function fetchErrorCodes() {

        axios.get('api/v1/error-codes/filter-info/' + dataset.value).then((response) => {
            let returnData: BFieldSelection[] = [];
            
            if (response && response.data && response.data.length > 0) {
                response.data.forEach(function (item: any) {
                    let itemToAdd: BFieldSelection = {
                        text: item.label,
                        value: item.code
                    };
                        returnData.push(itemToAdd);
                });
            }
            errorCodes.value = returnData;

            var errorCodeColumn = getErrorsAndWarningsColumns(false,true,false).find(x => x.key === "errorCode") as BTableColumn;
            if (errorCodeColumn && errorCodeColumn.filterable) {
                errorCodeColumn.filterable.editorOptions = errorCodes.value;
            }
        });
    }

    function fetchErrorDescriptions() {
        axios.get(`${apiUrl.value}/error-descriptions`).then((response) => {
            if (response && response.data && response.data.length > 0) {
                errorDescriptions.value = response.data as ErrorDescription[];
            }
        });            
    }

    function getErrorsAndWarningsForUploadDetails(tableOptions:BTableOptions,filterInfo: BFilter[], filterableFields: BFilterableField[]) {
        
        let fullUrl = uploadUrlBase;
        fullUrl += `/${dataset.value}/details/errors`;

        return getErrorsAndWarningsBase(fullUrl, tableOptions,filterInfo, filterableFields);
    }
    
    function getErrorsAndWarnings(tableOptions:BTableOptions,filterInfo: BFilter[], filterableFields: BFilterableField[]) {
        let url = `${ewUrlBase}/${dataset.value}`;
        return getErrorsAndWarningsBase(url, tableOptions, filterInfo, filterableFields);
    }

    function getErrorsAndWarningsBase(url:string, tableOptions:BTableOptions,filterInfo: BFilter[], filterableFields: BFilterableField[]) {
        if(dataset.value === undefined){
            return undefined;
        }

        let filterText = filterInfo ? serializeFilters(filterInfo, filterableFields) : '';
        let sortOptionsText = serializeSortBy(tableOptions.sortBy);        

        return axios.get(url!, { params: { filter: filterText, sort: sortOptionsText, page: tableOptions.page, pageSize: tableOptions.itemsPerPage, pagingMode: tableOptions.pagingMode } }); 
    }

    function getFiles(tableOptions:BTableOptions, filterInfo: BFilter[], filterableFields: BFilterableField[]) {
        
        if(dataset.value === undefined){
            return undefined;
        }
        let filterText = filterInfo ? serializeFilters(filterInfo, filterableFields): '';
        let sortOptionsText = serializeSortBy(tableOptions.sortBy);

        let fullUrl = uploadUrlBase;
        fullUrl += `/${dataset.value}`;

        return axios.get(fullUrl, { params: { filter: filterText, sort: sortOptionsText, page: tableOptions.page, pageSize: tableOptions.itemsPerPage, pagingMode: tableOptions.pagingMode } });
    }
    
    function getRecordDetails(id: number) {
        if(dataset.value === undefined){
            return undefined;
        }

        let fullUrl = uploadUrlBase;
        fullUrl += `/${dataset.value}/details/${id}`;
        return axios.get<UploadedFileDetails>(fullUrl);
    }
    
    function getValidationMessagesForFile(id: number, pageNumber: number, pageSize: number, filterInfo: BFilter[], filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        let filterText = serializeFilters(filterInfo, filterableFields);
        let sortText = serializeSortBy(sortingInfo);

        let fullUrl = apiUrl.value;
        fullUrl += `/${id}/validation-messages/page-number/${pageNumber}/page-size/${pageSize}`;

        return axios.get(fullUrl, { params: { filter: filterText, sort: sortText } });
    }
    
    function hiddenCssClass(condition: boolean) {
        if (condition) {
            return "d-none";
        }
    
        return "";
    }

    function getFileUploadColumns(onlyHasOneOri:boolean,submissionPeriodHidden:boolean){
        var filteredColumns = fileUploadColumns;

        if(onlyHasOneOri) {
            filteredColumns = filteredColumns.filter((col) => col.key != "agency");
        }
        
        if(submissionPeriodHidden) {
            filteredColumns = filteredColumns.filter((col) => col.key != "submissionPeriod");
        }

        return filteredColumns;
    }

    function getFilterableFieldsForFileUploads(onlyHasOneOri:boolean,submissionPeriodHidden:boolean){
        var columns = getFileUploadColumns(onlyHasOneOri, submissionPeriodHidden);
        var result = [] as BFilterableField[];

        columns.filter((col:BTableColumn) => col.filterable != undefined).forEach((col:BTableColumn) => result.push(col.filterable as BFilterableField));

        return result;
    }

    async function checkIfDownloadValid(downloadUrl:string){
        let testUrl = `${downloadUrl}${downloadUrl.indexOf('?') == -1?'?':'&'}tryDownload=y`;    

        try{
            let result = await getHtmlContent(testUrl);            

            return result != "" && (result == 'true' || (result.length > 0 && result.toLowerCase().indexOf('http error') == -1 && result != 'false'));
        }
        catch(error){
            return false;
        }
    }

    return {apiUrl, dataset, errorCodes, errorDescriptions, getTypes, errorDescriptionsDownloadUrl, getDefaultFilterableFields, getErrorsAndWarningsColumns, getFilterableFieldsForErrorsAndWarnings, fetchErrorCodes, 
        fetchErrorDescriptions, getErrorsAndWarnings, getErrorsAndWarningsForUploadDetails, getFiles, getRecordDetails, getValidationMessagesForFile, getFileUploadColumns, getFilterableFieldsForFileUploads, checkIfDownloadValid}
};